import { useEffect, useState } from "react"
import Sticky from "react-stickynode"
import styled from "styled-components"
import { useRouter } from "next/router"
import { themeGet } from "@styled-system/theme-get"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import axios from "axios"
import { getApiUrlLocale } from "@/helpers"
import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import Footer from "@/containers/common/FooterNew"
import MetaData from "@/common/components/MetaData"
import SectionHeading from "@/common/components/SectionHeading"
import SectionLine from "@/common/components/SectionLine"
import Link from "next/link"
import Button from "@/common/components/Button"
import Container from "@/common/components/UI/Container"
import { AppWrapper, ContentWrapper } from "@/containers/common/style"
import MediumArticleCard from "@/common/components/MediumArticleCard"
import AssetCard from "@/common/components/AssetCard"

export const getStaticProps = async ({ locale }) => {
  const res = await axios(
    `${
      process.env.NEXT_PUBLIC_CMS_HOST
    }/api/media-coverages?populate=*&locale=${getApiUrlLocale(
      locale
    )}&sort[0]=date%3Adesc`,
    {
      headers: {
        "Cache-Control": "s-maxage=1, stale-while-revalidate"
      }
    }
  )
  const data = await res.data

  return {
    props: {
      press: data,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

const Press = ({ press }) => {
  const { t } = useTranslation("common")
  const router = useRouter()
  const [activeTab, setActiveTab] = useState("media-coverage")

  useEffect(() => {
    {
      if (router.asPath === "/press#brand-assets") {
        setActiveTab("brand-assets")
      }
      if (router.asPath === "/press#media-coverage") {
        setActiveTab("media-coverage")
      }
    }
  }, [router.asPath])

  const brandAssetsCover = [
    {
      id: 0,
      title: "Logos",
      alt: "logos_cover",
      src: "/brand_assets/cover_images/logo.jpg",
      fileSrc: "/brand_assets/kryptogo_logos.zip"
    },
    {
      id: 1,
      title: "Leadership",
      alt: "leadership_cover",
      src: "/brand_assets/cover_images/leadership.jpg",
      fileSrc: "/brand_assets/kryptogo_leadership.zip"
    },
    {
      id: 2,
      title: "Company",
      alt: "company_cover",
      src: "/brand_assets/cover_images/company.jpeg",
      fileSrc: "/brand_assets/kryptogo_company.zip"
    }
  ]

  return (
    <div>
      <>
        <MetaData
          title={t("press.meta.title")}
          description={t("press.meta.description")}
          url={t("press.meta.url")}
          image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
          languageAlternates={[
            [
              {
                hrefLang: "en-US",
                href: "https://www.kryptogo.com/press"
              },
              {
                hrefLang: "zh-TW",
                href: "https://www.kryptogo.com/tw/press"
              },
              {
                hrefLang: "zh-CN",
                href: "https://www.kryptogo.com/cn/press"
              }
            ]
          ]}
        />

        <ResetCSS />
        {/* end of global and reset style */}

        {/* start app classic landing */}
        <AppWrapper>
          <Sticky top={0} innerZ={1000} activeClass="sticky-active">
            <NavbarNew />
          </Sticky>
          <Container>
            <TitleWrapper>
              <SectionHeading
                title={t("navbar.resources.press.title")}
                slogan=""
              />
            </TitleWrapper>
            <ContentWrapper>
              <ButtonWrapper>
                <TabButtons
                  className="tabButton"
                  id="press-media-coverage"
                  title={t("press.media-coverage")}
                  tab="media-coverage"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  className="tabButton"
                  id="press-media-brand-assets"
                  title={t("press.brand-assets")}
                  tab="brand-assets"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </ButtonWrapper>

              {activeTab === "media-coverage" && (
                <Cards>
                  {press?.data?.map(item => (
                    <CardWrapper key={item.id}>
                      <MediumArticleCard
                        t={t}
                        img={
                          item.attributes.cover_image.data?.[0].attributes
                            .formats?.medium?.url ||
                          item.attributes.cover_image.data?.[0].attributes
                            .formats?.small?.url
                        }
                        title={item.attributes.title}
                        desc={item.attributes.description}
                        link={item.attributes.external_link}
                        linkText={item.attributes.external_link_text}
                        date={item.attributes.date}
                      />
                    </CardWrapper>
                  ))}
                </Cards>
              )}

              {activeTab === "brand-assets" && (
                <Cards>
                  {brandAssetsCover?.map(cover => (
                    <CardWrapper key={cover.id}>
                      <AssetCard
                        title={cover.title}
                        src={cover.src}
                        fileSrc={cover.fileSrc}
                      />
                    </CardWrapper>
                  ))}
                </Cards>
              )}
            </ContentWrapper>

            <SectionLine />
          </Container>
          <Footer />
        </AppWrapper>
      </>
    </div>
  )
}

export default Press

const TabButtons = ({ tab, mr, title, activeTab, setActiveTab }) => {
  const router = useRouter()
  const tabHandler = () => {
    setActiveTab(tab)
  }

  return (
    <Link href={`#${tab}`} passHref legacyBehavior>
      <Button
        className={"tabButton"}
        type={activeTab === tab ? "" : "secondary"}
        title={title}
        onClick={tabHandler}
      />
    </Link>
  )
}
const ButtonWrapper = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  gap: 24px;

  @media (max-width: 800px) {
    justify-content: center !important;
    overflow: visible;
    flex-wrap: wrap;

    a {
      flex: 1 1 40%;
      button {
        width: 100%;
      }
      &:nth-child(1) {
        flex: 1 1 100%;
      }
    }

    .tabButton {
      flex: 1 1 40%;
      padding: 10px 15px;
      min-width: 0px;
      background-color: none !important;
      box-shadow: none;
      transform: scale(1) !important;

      &:hover,
      &:active {
        box-shadow: none;
        transform: scale(1) !important;
      }

      &:nth-child(1) {
        flex: 1 1 100%;
      }
    }
  }
`

const CardWrapper = styled.div`
  width: 50%;
  display: block;
  padding: 32px;
  max-width: calc(50%);
  flex: 1 1 50%;
  @media screen and (max-width: 1200px) {
    padding: 1rem;
    margin-bottom: 1rem;
    flex: 0 1 50%;
    max-width: 50%;
  }
  @media screen and (max-width: 768px) {
    max-width: 400px;
    margin: 0 auto;
    flex: 0 1 100%;
    padding: 1rem 5px;
    width: 100%;
    border-bottom: 1px solid ${themeGet("colors.primaryLight")};
  }
`
const TitleWrapper = styled.div`
  padding-top: 160px;
  margin-bottom: var(--textGap);
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`

const FormWrapper = styled.form`
  display: flex;
  justify-content: space-between;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  flex-wrap: wrap;
  .required {
    color: ${themeGet("colors.primary")};
    margin-left: 4px;
  }
  div {
    margin: 1rem 0rem;
    width: 48%;
    &.fullWidth {
      width: 100%;
    }
    @media (max-width: 640px) {
      margin: 0 0 2rem;
      width: 100%;
    }
  }
  label {
    display: flex;
    flex-wrap: wrap;
    p {
      order: -1;
      transition: 0.3s ease;
    }
    &:hover {
      p {
        color: ${themeGet("colors.title")};
      }
    }
  }
  input,
  textarea {
    font-size: 20px;
    width: 100%;
    padding: 4px 8px;
    color: #fff;
    background-color: transparent;
    outline: none;
    border: none;
    border-bottom: 1px solid #fff;
    transition: 0.3s ease;
    &:focus {
      border-color: ${themeGet("colors.primary")};
      ~ p {
        color: ${themeGet("colors.primary")};
      }
    }
    &.error {
      border-color: red;
      ~ p {
        color: red;
      }
    }
    @media (max-width: 640px) {
      padding: 0;
    }
  }
  .CheckBoxWrap {
    justify-content: center;
    padding: 20px 0;
    cursor: pointer;
    transition: 0.3s ease;
    flex-wrap: nowrap;
    @media (max-width: 768px) {
      padding: 0;
    }
    p {
      order: 2;
    }
    input[type="checkbox"] {
      width: auto !important;
      margin: 10px;
      &:focus ~ p {
        color: ${themeGet("colors.text")};
      }
    }
    a {
      color: ${themeGet("colors.primary")};
      margin-left: 10px;
      border-bottom: 1px solid;
      display: inline;
    }
  }
  button[type="submit"] {
    border-bottom: none;
    border-radius: 100px;
    margin: 15px auto;
  }
`

const MediaInquiry = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  margin: 3rem 0rem;

  .contactBox {
    border: 1px solid rgba(255, 194, 17, 0.5);
    border-radius: 30px;
    box-shadow: 0px 8px 20px rgba(245, 189, 33, 0.483);
    padding: 80px;
    width: 100%;

    h3 {
      color: rgba(255, 194, 17, 1);
      font-size: 2rem;

      @media (max-width: 640px) {
        font-size: 1.5rem;
      }
    }

    @media (max-width: 640px) {
      padding: 30px;
    }
  }

  .tCenter {
    text-align: center;
  }

  .max {
    margin: 0 auto !important;
    width: 100% !important;
  }

  .center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .underLine {
    border-bottom: 1px solid rgba(255, 194, 17, 1);
    padding-bottom: 50px;
    margin-bottom: 30px;

    @media (max-width: 640px) {
      padding-bottom: 25px;
    }
  }

  h3.tCenter {
    margin-top: 0px;
  }

  .CheckBoxWrap p {
    margin-top: 0px;
  }

  .imgWrapper {
    background-color: #ccc;
    width: 100%;
    height: 300px;
    overflow: hidden;
    background-color: #001f58;
    border-radius: 15px;
    position: relative;

    .img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: 0.5s ease;

      &.asset {
        object-fit: contain;
      }
    }

    @media (max-width: 1024px) {
      height: 200px;
    }
  }
`
const Cards = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: 1200px;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    .cards {
      max-width: 450px;
      margin-top: 28px;
    }
  }
`
const InquiryButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
